
import React, { useState, useEffect } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { SessionProvider, useSession } from './context/SessionContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SelectionPage } from './components/SelectionPage';
import { LandingPage } from './components/LandingPage';
import { UserRoutes } from './routes/UserRoutes';
import { WorkerRoutes } from './routes/WorkerRoutes';
import { WorkerLogin } from './routes/WorkerLogin';
import { SplashScreen } from './components/SplashScreen';
import { LanguageSelection } from './components/LanguageSelection';
import { UserRole } from './types';
import { AuthCallback } from './components/AuthCallback';
import { AdminRoutes } from './routes/AdminRoutes';
import { NotificationManager } from './components/NotificationManager';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';
import { App as CapApp } from '@capacitor/app';
import { notificationService } from './services/notificationService';
import { requestAllPermissionsOnFirstLaunch } from './services/permissionsService';
import { WorkerIncomingCallScreen } from './components/WorkerIncomingCallScreen';
import { JobRequest } from './types';

const AppContent: React.FC = () => {
    const {
        showSplash, isSessionLoading,
        hasSelectedLang, language, setLanguage, setHasSelectedLang,
        userProfile, feeConfig, setFeeConfig, serviceCategories, setUserProfile,
        setIsLoggedIn, isLoggedIn, reloadSession
    } = useSession();

    // Initialize Capacitor features + request all permissions
    useEffect(() => {
        // ✅ Request all permissions on first launch (notifications, location, storage/media, microphone)
        setTimeout(() => {
            requestAllPermissionsOnFirstLaunch().catch(console.error);
        }, 1500);

        if (Capacitor.isNativePlatform()) {
            // CRITICAL: Status Bar Configuration (Fix Notch & Status Bar)
            StatusBar.setOverlaysWebView({ overlay: false }).catch(e => console.error(e));

            // Dynamic Status Bar Theme Handling
            const applyTheme = async () => {
                const isDark = document.documentElement.classList.contains('dark') ||
                    window.matchMedia('(prefers-color-scheme: dark)').matches;

                try {
                    if (isDark) {
                        await StatusBar.setStyle({ style: Style.Dark });
                        await StatusBar.setBackgroundColor({ color: '#0f172a' });
                    } else {
                        await StatusBar.setStyle({ style: Style.Light });
                        await StatusBar.setBackgroundColor({ color: '#FFFFFF' });
                    }
                } catch (e) {
                    console.error('❌ Status Bar Theme Error:', e);
                }
            };

            applyTheme();
            const mq = window.matchMedia('(prefers-color-scheme: dark)');
            mq.addEventListener('change', applyTheme);

            // Also watch for in-app dark mode toggle (class changes on <html>)
            const observer = new MutationObserver(() => applyTheme());
            observer.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['class']
            });

            StatusBar.show().catch(e => console.error(e));

            // Fix Keyboard accessory issues
            Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(e => console.error(e));

            // CRITICAL: Handle OAuth Redirect + Deep Links
            CapApp.addListener('appUrlOpen', async (data: { url: string }) => {
                console.log('🔗 Deep link opened:', data.url);

                if (data.url.includes('/auth/callback') || data.url.includes('#access_token') || data.url.includes('access_token=')) {
                    console.log('✅ OAuth callback detected');

                    try {
                        const { Browser } = await import('@capacitor/browser');
                        await Browser.close();
                    } catch (e) {
                        console.log('Browser already closed or not available');
                    }

                    let hash = '';
                    try {
                        const urlString = data.url.replace('humhena://auth/callback', 'https://humhena.com/auth/callback');
                        const url = new URL(urlString);
                        hash = url.hash;

                        if (!hash && url.searchParams.has('access_token')) {
                            hash = `#access_token=${url.searchParams.get('access_token')}&expires_in=${url.searchParams.get('expires_in')}&refresh_token=${url.searchParams.get('refresh_token')}&token_type=${url.searchParams.get('token_type')}`;
                        }
                    } catch (urlParseError) {
                        console.log('⚠️ URL parsing failed, extracting hash manually');
                        const hashIndex = data.url.indexOf('#');
                        if (hashIndex !== -1) hash = data.url.substring(hashIndex);
                    }

                    if (hash) {
                        console.log('📝 Processing OAuth hash:', hash);
                        window.location.hash = hash;

                        setTimeout(async () => {
                            try {
                                await reloadSession();
                                console.log('✅ Session Reloaded via OAuth Flow');
                            } catch (e) {
                                console.error('❌ Failed to process OAuth callback:', e);
                            }
                        }, 500);
                    } else {
                        console.error('❌ No hash found in OAuth callback URL');
                    }
                }
            });

            // Handle Back Button
            CapApp.addListener('backButton', ({ canGoBack }) => {
                if (!canGoBack) {
                    CapApp.exitApp();
                } else {
                    window.history.back();
                }
            });

            return () => {
                mq.removeEventListener('change', applyTheme);
                CapApp.removeAllListeners();
            };
        }
    }, [reloadSession]);

    // Local state to track if user has clicked "Get Started" - persist in localStorage
    const [hasSeenLanding, setHasSeenLanding] = useState(() => {
        return localStorage.getItem('hh_seen_landing') === 'true';
    });

    // Persist hasSeenLanding to localStorage
    useEffect(() => {
        if (hasSeenLanding) {
            localStorage.setItem('hh_seen_landing', 'true');
        }
    }, [hasSeenLanding]);

    // Handle Incoming Job Call
    const [incomingJob, setIncomingJob] = useState<JobRequest | null>(null);

    useEffect(() => {
        notificationService.setJobRequestCallback((jobData: any) => {
            console.log("App: Incoming Job Call Triggered", jobData);
            // Construct a temporary JobRequest object for the UI
            const jobRequest: any = {
                id: jobData.jobId,
                customerName: jobData.customerName,
                price: jobData.price,
                skill: jobData.skill,
                description: jobData.description || 'New Job Request',
                customerAvatar: jobData.customerAvatar, // If passed
                status: 'PENDING',
                createdAt: new Date().toISOString()
            };
            setIncomingJob(jobRequest);
        });
    }, []);

    // ✅ FIX: Accept job - navigate instantly, no delay
    const handleAcceptJob = () => {
        const jobId = incomingJob?.id;
        setIncomingJob(null);
        // Use hash routing for native app
        if (jobId) {
            window.location.hash = '#/worker/jobs';
        }
    };

    const handleDeclineJob = () => {
        setIncomingJob(null);
    };

    // Initialize notification service on app load
    useEffect(() => {
        notificationService.initialize().then((success) => {
            if (success) {
                console.log('✅ Notification service initialized');
            } else {
                console.warn('⚠️ Notification service failed to initialize');
            }
        });

        // Cleanup on unmount
        return () => {
            notificationService.stopKeepAlive();
        };
    }, []);

    // Handle splash / loading globally
    if (showSplash || isSessionLoading) {
        return <SplashScreen onFinish={() => { }} />;
    }

    // INCOMING CALL OVERLAY (Global Priority)
    if (incomingJob) {
        return (
            <WorkerIncomingCallScreen
                job={incomingJob}
                onAccept={handleAcceptJob}
                onDecline={handleDeclineJob}
            />
        );
    }

    // Check if we are on an admin route
    const isAdminRoute = window.location.hash.includes('#/admin') || window.location.pathname.includes('/admin');

    // Check if OAuth is in progress
    const hasPendingOAuth = !!localStorage.getItem('hh_pending_role');

    // 1. Show Landing Page First (if not Admin and not during OAuth)
    if (!hasSeenLanding && !isAdminRoute && !hasPendingOAuth && !isLoggedIn) {
        console.log('📄 Showing Landing Page');
        // If config is loaded, show landing. If not, it might skip or wait (usually loaded during splash)
        if (feeConfig) {
            return <LandingPage onGetStarted={() => setHasSeenLanding(true)} config={feeConfig} />;
        }
    }

    // 2. Show Language Selection Next (SKIP if logged in, OAuth callback, or pending OAuth role)
    const isOAuthCallback = window.location.hash.includes('#access_token') ||
        window.location.pathname.includes('/auth/callback') ||
        window.location.hash.includes('/auth/callback');
    const shouldShowLanguage = !hasSelectedLang && !isAdminRoute && !isLoggedIn && !isOAuthCallback && !hasPendingOAuth;

    if (shouldShowLanguage) {
        console.log('🌐 Showing Language Selection');
        return <LanguageSelection currentLanguage={language} onSelect={setLanguage} onConfirm={() => setHasSelectedLang(true)} />;
    }

    console.log('✅ Proceeding to Routes (Landing/Language skipped)');

    return (
        <Routes>
            <Route path="/" element={<SelectionPage />} />

            <Route path="/user/*" element={<UserRoutes />} />

            <Route path="/worker/login" element={<WorkerLogin />} />
            <Route path="/worker/*" element={
                <ProtectedRoute>
                    <WorkerRoutes />
                </ProtectedRoute>
            } />

            {/* Admin Routes */}
            <Route path="/admin/*" element={<AdminRoutes />} />

            {/* OAuth Callback Handle */}
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};

import { ThemeProvider } from './context/ThemeContext';

const App: React.FC = () => {
    // Hybrid Routing Logic:
    console.log("App Mounting...");
    const isNative = Capacitor.isNativePlatform();
    const useHashRouter = window.location.hash.includes('#/') || isNative;
    const Router = useHashRouter ? HashRouter : BrowserRouter;

    return (
        <Router>
            <ThemeProvider>
                <SessionProvider>
                    <NotificationManager />
                    <ErrorBoundary>
                        <AppContent />
                    </ErrorBoundary>
                </SessionProvider>
            </ThemeProvider>
        </Router>
    );
};

export default App;
